---
title: "What Is Cryptocurrency?"
slug: "what-is-cryptocurrency"
excerpt: ""
publishedAt: "2026-05-25"
updatedAt: "2026-05-25"
author: "maya-lestari"
category: "Technology"
tags:
  - bitcoin
  - ethereum
  - finance
  - blockchain
featuredImage: "https://builtin.com/sites/www.builtin.com/files/styles/ckeditor_optimize/public/inline-images/inside-crypto-cryptocurrency.png"
status: "published"
draft: false
---

## Infobox

<InfoBox title="Cryptocurrency">
<ul>
<li><strong>Type:</strong> Digital currency</li>
<li><strong>Inception:</strong> 2009 (Bitcoin)</li>
<li><strong>Key Features:</strong> Decentralized, Blockchain-based, Peer-to-Peer</li>
<li><strong>Primary Use:</strong> Medium of exchange, Investment, Store of value</li>
<li><strong>Notable Examples:</strong> Bitcoin (BTC), Ethereum (ETH), Tether (USDT)</li>
<li><strong>Technology:</strong> Blockchain, Cryptography, Consensus Algorithms</li>
</ul>
</InfoBox>

## Overview

Cryptocurrency represents a paradigm shift in financial systems by eliminating intermediaries such as banks or governments in transactions. Unlike traditional fiat currencies issued by central authorities, cryptocurrencies are typically generated through mining or other consensus mechanisms and are stored in digital wallets. Their value is determined by market demand, utility, and scarcity, often leading to high volatility.

Most cryptocurrencies are built on blockchain, a distributed ledger technology that records transactions across multiple computers in a way that ensures security and immutability. This structure makes it extremely difficult to alter historical data, enhancing trust among users. Cryptocurrencies facilitate fast, borderless transactions with relatively low fees, making them attractive for international payments and remittances.

## History / Background

The concept of digital currency predates Bitcoin, with early attempts such as [DigiCash](# "DigiCash") in the 1980s and [e-gold](# "e-gold") in the 1990s. However, these systems relied on centralized entities and ultimately failed due to regulatory and operational challenges.

The modern era of cryptocurrency began in 2009 with the launch of [Bitcoin](# "Bitcoin"), created by an anonymous entity known as [Satoshi Nakamoto](# "Satoshi Nakamoto"). Bitcoin introduced the first decentralized blockchain and a proof-of-work consensus mechanism, solving the double-spending problem without a central authority. Its whitepaper, titled *Bitcoin: A Peer-to-Peer Electronic Cash System*, outlined a vision for a trustless, censorship-resistant monetary system.

Following Bitcoin’s success, thousands of alternative cryptocurrencies—known as altcoins—emerged, each with unique features. Notable examples include Ethereum (2015), which introduced smart contracts and decentralized applications (dApps), and Ripple (2012), designed for fast cross-border payments. The rise of Initial Coin Offerings (ICOs) in 2017–2018 further expanded the ecosystem, though many projects were later deemed fraudulent or failed.

## How It Works

Cryptocurrencies operate using a combination of cryptographic techniques and decentralized networks. The core components include:

- **Blockchain:** A distributed ledger that records all transactions in chronological order across a network of nodes. Each block contains a cryptographic hash of the previous block, ensuring chain integrity.
- **Cryptography:** Public-key cryptography generates digital signatures that verify transactions and wallet ownership. Private keys are used to authorize transactions, while public keys serve as addresses.
- **Consensus Mechanisms:** Protocols like Proof of Work (PoW), Proof of Stake (PoS), and Delegated Proof of Stake (DPoS) validate transactions and add new blocks to the blockchain. PoW, used by Bitcoin, requires computational power; PoS, used by Ethereum 2.0, relies on staked tokens.
- **Decentralization:** No single entity controls the network. Instead, participants (miners, validators, or nodes) collectively maintain and secure the system.
 
When a user initiates a transaction, it is broadcast to the network and verified by nodes. Once confirmed, the transaction is added to a block and appended to the blockchain. Miners or validators are rewarded with newly minted coins or transaction fees, incentivizing network participation.

## Important Facts

<FactList>
<ul>
<li><strong>Limited Supply:</strong> Many cryptocurrencies, like Bitcoin, have a capped supply (e.g., 21 million BTC), designed to prevent inflation and mimic scarce resources like gold.</li>
<li><strong>Pseudonymity:</strong> Transactions are recorded on a public ledger, but identities are often obscured behind wallet addresses, offering a degree of privacy.</li>
<li><strong>Irreversibility:</strong> Once confirmed, cryptocurrency transactions cannot be reversed, making them irreversible and reducing fraud risks for merchants.</li>
<li><strong>Volatility:</strong> Cryptocurrency prices can fluctuate dramatically due to speculation, regulatory news, or market sentiment, posing risks for investors.</li>
<li><strong>Regulation:</strong> Governments worldwide are developing frameworks to regulate cryptocurrencies, addressing concerns like money laundering, tax evasion, and consumer protection.</li>
<li><strong>Energy Consumption:</strong> PoW-based cryptocurrencies like Bitcoin consume significant energy, leading to debates about environmental sustainability and the shift toward greener alternatives like PoS.</li>
</ul>
</FactList>

## Timeline

<Timeline items={[
  { date: "1983", title: "David Chaum introduces DigiCas", description: "David Chaum introduces [DigiCash](# \"DigiCash\"), an early digital currency system." },
  { date: "1996", title: "NSA publishes a paper", description: "NSA publishes a paper on [Hashcash](# \"Hashcash\"), a proof-of-work system influencing Bitcoin." },
  { date: "2009", title: "Bitcoin is launched by", description: "Bitcoin is launched by Satoshi Nakamoto, mining the first block (Genesis Block)." },
  { date: "2011", title: "Namecoin and Litecoin are", description: "Namecoin and Litecoin are created as alternatives to Bitcoin." },
  { date: "2015", title: "Ethereum is launched, introduc", description: "Ethereum is launched, introducing smart contracts and dApps." },
  { date: "2017", title: "Bitcoin reaches a record", description: "Bitcoin reaches a record high of nearly $20,000; ICO boom begins." },
  { date: "2020", title: "Ethereum transitions to Ethere", description: "Ethereum transitions to Ethereum 2.0, adopting Proof of Stake." },
  { date: "2021", title: "El Salvador becomes the", description: "El Salvador becomes the first country to adopt Bitcoin as legal tender." },
  { date: "2022", title: "FTX, a major cryptocurrency", description: "FTX, a major cryptocurrency exchange, collapses, triggering a market downturn." },
  { date: "2023", title: "Bitcoin ETFs are approved", description: "Bitcoin ETFs are approved in the U.S., increasing institutional adoption." }
]} />

## Related Terms

<RelatedTerms>
<ul>
<li><strong>Bitcoin (BTC)</strong> – The first and most widely known cryptocurrency.</li>
<li><strong>Ethereum (ETH)</strong> – A blockchain platform enabling smart contracts and dApps.</li>
<li><strong>Altcoin</strong> – Any cryptocurrency other than Bitcoin.</li>
<li><strong>Stablecoin</strong> – A cryptocurrency pegged to a stable asset like the U.S. dollar.</li>
<li><strong>Smart contract</strong> – Self-executing contracts with terms written in code.</li>
<li><strong>Decentralized Finance (DeFi)</strong> – Financial services built on blockchain without intermediaries.</li>
<li><strong>Non</strong> – Fungible Token (NFT) – A unique digital asset representing ownership of a specific item.</li>
<li><strong>Mining</strong> – The process of validating transactions and adding blocks to a blockchain.</li>
<li><strong>Cryptocurrency wallet</strong> – A digital tool for storing and managing cryptocurrency keys.</li>
<li><strong>Blockchain</strong> – The underlying technology enabling decentralized and secure transactions.</li>
</ul>
</RelatedTerms>

## FAQ

<FAQ>
<details>
<summary>Is cryptocurrency legal?</summary>
<p>Legality varies by country. Some nations fully regulate or adopt cryptocurrencies (e.g., El Salvador), while others ban or restrict their use.</p>
</details>
<details>
<summary>How do I buy cryptocurrency?</summary>
<p>Cryptocurrencies can be purchased on exchanges like Coinbase, Binance, or Kraken using fiat currency or other cryptocurrencies. Peer-to-peer platforms and ATMs are also options.</p>
</details>
<details>
<summary>What is a private key?</summary>
<p>A private key is a cryptographic code that allows users to access and spend their cryptocurrency. It must be kept secure, as losing it may result in permanent loss of funds.</p>
</details>
<details>
<summary>Can cryptocurrency be hacked?</summary>
<p>While blockchains are highly secure, exchanges and wallets can be vulnerable to hacks. Users should employ strong passwords, two-factor authentication, and hardware wallets for protection.</p>
</details>
<details>
<summary>What is the difference between Bitcoin and Ethereum?</summary>
<p>Bitcoin is primarily a digital currency and store of value, while Ethereum is a programmable blockchain supporting smart contracts and decentralized applications.</p>
</details>
<details>
<summary>How are cryptocurrency transactions taxed?</summary>
<p>Tax treatment varies by jurisdiction. In many countries, cryptocurrency is subject to capital gains tax when sold or traded. Income tax may apply to mining rewards or payments received in crypto.</p>
</details>
</FAQ>

## References

<ReferenceList>
<ol>
<li>Nakamoto, S. (2009). *Bitcoin: A Peer-to-Peer Electronic Cash System*. Bitcoin whitepaper.</li>
<li>Antonopoulos, A. M. (2014). *Mastering Bitcoin*. O’Reilly Media.</li>
<li>Tapscott, D., &amp; Tapscott, A. (2016). *Blockchain Revolution: How the Technology Behind Bitcoin Is Changing Money, Business, and the World*. Penguin.</li>
<li>European Central Bank. (2019). *Crypto-Assets: Implications for Financial Stability, Monetary Policy, and Payments and Market Infrastructure*.</li>
<li>U.S. Securities and Exchange Commission. (2022). *Crypto Assets and Cybersecurity Risks*.</li>
</ol>
</ReferenceList>

